import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { emailValidator } from 'src/app/directives/email/email.directive';
import { phoneNumberValidator } from 'src/app/directives/phone-number/phone-number.directive';
import { zipCodeValidator } from 'src/app/directives/zip-code/zip-code.directive';
import { handleErrorMessage } from 'src/app/helpers/error-handler.helper';
import { DataLayerService } from 'src/app/services/analytics/data-layer.service';
import {
  Dropdown,
  DropdownMenus,
  EmploymentStatusType,
} from 'src/app/services/dropdowns/dropdowns.model';
import { DropdownsService } from 'src/app/services/dropdowns/dropdowns.service';
import { ErrorMessageService } from 'src/app/services/error-message/error-message.service';
import { FinalFurnitureLoanApplication } from 'src/app/services/furniture-credit/furniture-credit.model';
import { FurnitureCreditService } from 'src/app/services/furniture-credit/furniture-credit.service';

@Component({
  selector: 'app-furniture-applicant',
  templateUrl: './furniture-applicant.component.html',
  styleUrls: ['./furniture-applicant.component.scss'],
})
export class FurnitureApplicantComponent implements OnInit, OnDestroy {
  applicantFormGroup: FormGroup;
  furnitureLoanApplication: FinalFurnitureLoanApplication | null = null;
  locations: Array<Dropdown> = [];
  dropdownMenus: DropdownMenus | null = null;
  loading = false;
  isSaving = false;
  isDirtyAndMissingRequiredFields = false;

  private subscriptions: Array<Subscription> = [];

  constructor(
    private formBuilder: FormBuilder,
    private dropdownsService: DropdownsService,
    private creditService: FurnitureCreditService,
    private errorMessageService: ErrorMessageService,
    private router: Router,
    private titleService: Title,
    private dataLayerService: DataLayerService
  ) {
    this.titleService.setTitle('Sims Furniture | Applicant Information');
    this.applicantFormGroup = this.buildFormGroup();
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.creditService.finalFurnitureLoanApplication.subscribe(
        (data: FinalFurnitureLoanApplication | null) => {
          if (data) {
            this.furnitureLoanApplication = { ...data };

            if (data.LoanApplication) {
              this.setFormValues(data.LoanApplication);
            }

            if (data.Applicant) {
              this.setFormValues(data.Applicant);
            }

            if (data.ApplicantAddress) {
              this.setFormValues(data.ApplicantAddress);
            }

            if (
              data.ApplicantEmployment &&
              data.ApplicantEmployment.EmploymentStatus
            ) {
              this.applicantFormGroup.controls['EmploymentStatus'].setValue(
                data.ApplicantEmployment.EmploymentStatus
              );
            }
          }
        }
      )
    );

    this.subscriptions.push(
      this.dropdownsService.dropdownMenus.subscribe(
        (data: DropdownMenus | null) => {
          if (data) {
            this.dropdownMenus = { ...data };
            this.locations = [...data.locations];
          }
        }
      )
    );

    this.subscriptions.push(
      this.dropdownsService.loading.subscribe((isLoading: boolean) => {
        this.loading = isLoading;
      })
    );

    this.subscriptions.push(
      this.applicantFormGroup.statusChanges.subscribe((status: string) => {
        if (status && status === 'VALID') {
          this.isDirtyAndMissingRequiredFields = false;
        }
      })
    );

    if (!this.dropdownMenus) {
      this.dropdownsService.getDropdowns();
    }
  }

  ngOnDestroy(): void {
    if (this.subscriptions.length > 0) {
      this.subscriptions.forEach((sub) => {
        sub.unsubscribe();
      });
    }
  }

  buildFormGroup(): FormGroup {
    return this.formBuilder.group({
      DomainID: ['', Validators.required],
      RequestPurpose: ['', Validators.required],
      ContactFirst: ['', Validators.required],
      ContactLast: ['', Validators.required],
      ContactMiddle: [''],
      ContactSuffix: [{ value: '', disabled: true }],
      PhoneNumber: ['', [Validators.required, phoneNumberValidator]],
      PhoneNumberType: ['', Validators.required],
      EmailAddress: ['', [Validators.required, emailValidator]],
      Address: ['', Validators.required],
      AptNumber: [''],
      POBox: [{ value: '', disabled: true }],
      City: ['', Validators.required],
      State: ['', Validators.required],
      ZipCode: ['', [Validators.required, zipCodeValidator]],
      EmploymentStatus: ['', Validators.required],
      LivingSituation: ['', Validators.required],
      Years: ['', [Validators.required, Validators.min(0)]],
      Months: [
        '',
        [Validators.required, Validators.min(0), Validators.max(12)],
      ],
      Over18: [false, Validators.requiredTrue],
      PrivacyPolicy: [false, Validators.requiredTrue],
      CommunicationConsent: [false],
    });
  }

  setFormValues(data: any) {
    // Set form values
    Object.keys(data).forEach((key) => {
      // Check if key exists on current form.
      if (this.applicantFormGroup.controls[key]) {
        this.applicantFormGroup.controls[key].setValue(data?.[key]);
      }
    });
  }

  onEmploymentStatusChange(value: string) {
    if (
      value === EmploymentStatusType.unemployed ||
      value === EmploymentStatusType.retired
    ) {
      this.creditService.clearOnEmploymentStatusChange(value);
    }
  }

  async onNext() {
    this.isDirtyAndMissingRequiredFields = false;

    if (this.applicantFormGroup.invalid) {
      this.applicantFormGroup.markAllAsTouched();
      this.isDirtyAndMissingRequiredFields = true;
      return;
    }

    this.dataLayerService.logEvent(
      'button_clicked',
      'credit_application',
      'click',
      'Save Consent of Applicant Information'
    );

    const loan = this.creditService.setApplicantInformation(
      this.applicantFormGroup.value
    );

    await this.saveCreditApplication(loan);
  }

  onClearApplication() {
    this.dataLayerService.logEvent(
      'button_clicked',
      'credit_application',
      'click',
      'Clear Credit Application'
    );

    this.creditService.clearApplication();
    this.isDirtyAndMissingRequiredFields = false;

    // Resetting form causes Angular error: NG0100: Expression has changed after it was checked.
    // Setting form manually.
    this.applicantFormGroup = this.buildFormGroup();
  }

  private async saveCreditApplication(
    loan: FinalFurnitureLoanApplication
  ): Promise<void> {
    try {
      this.isSaving = true;

      const response = await this.creditService.postCreditApplication(loan);

      if (response) {
        this.creditService.setCreditAfterPost(response);
        this.router.navigateByUrl('/furniture/applicant/employer');
      }

      return;
    } catch (error) {
      this.errorMessageService.addToErrorMessages(handleErrorMessage(error));
    } finally {
      this.isSaving = false;
    }
  }
}
